import React from 'react';
import { ShieldCheck, CheckCircle2, MapPin, Award, ArrowRight, Wrench, Zap } from 'lucide-react';
import { useSolarStore } from '../../store/useSolarStore';

import solarRoofImg from '../../assets/images/solar_roof_nigeria_1786363112454.jpg';
import lithiumBankImg from '../../assets/images/lithium_power_bank_1786363126900.jpg';
import engineerImg from '../../assets/images/certified_solar_engineer_1786363140860.jpg';
import happyFamilyImg from '../../assets/images/happy_nigerian_family_solar_1786363436191.jpg';
import commercialImg from '../../assets/images/commercial_solar_business_1786363452201.jpg';

export const InstallationShowcase: React.FC = () => {
  const { setIsLeadModalOpen } = useSolarStore();

  return (
    <section className="bg-slate-900 border-y border-slate-800 py-12 sm:py-16 text-white overflow-hidden">
      <div className="w-full max-w-none space-y-12">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold tracking-wider uppercase">
            <Award className="w-3.5 h-3.5" /> Real Nigerian Field Installations & Proof
          </div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
            See Our COREN-Certified Solar Engineers In Action
          </h2>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
            From residential duplexes in Umuahia and Port Harcourt to commercial complexes in Lagos & Abuja, inspect the craftsmanship of our Tier-1 monocrystalline roof arrays, lithium power banks, and DB protection setups.
          </p>
        </div>

        {/* 3-Column Image Showcase Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Card 1: Rooftop Array */}
          <div className="bg-slate-950 border border-slate-800 rounded-3xl overflow-hidden shadow-xl hover:border-emerald-500/40 transition-all flex flex-col group">
            <div className="relative h-56 overflow-hidden">
              <img
                src={solarRoofImg}
                alt="Nigerian Solar Roof Installation"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                referrerPolicy="no-referrer"
              />
              <div className="absolute top-3 left-3 bg-slate-950/80 backdrop-blur-md border border-slate-800 text-emerald-400 text-[11px] font-bold px-3 py-1 rounded-full flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Umuahia / Abia State
              </div>
            </div>
            <div className="p-6 space-y-3 flex-grow flex flex-col justify-between">
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-white group-hover:text-emerald-400 transition-colors">
                  Monocrystalline Solar Panel Roof Array
                </h3>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Heavy-duty aluminium mounting rails engineered to withstand high West African winds, yielding maximum 25-year energy harvesting.
                </p>
              </div>
              <div className="pt-3 border-t border-slate-900 flex items-center justify-between text-xs text-emerald-400 font-semibold">
                <span>10.5kWp System Array</span>
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              </div>
            </div>
          </div>

          {/* Card 2: Lithium Power Room */}
          <div className="bg-slate-950 border border-slate-800 rounded-3xl overflow-hidden shadow-xl hover:border-emerald-500/40 transition-all flex flex-col group">
            <div className="relative h-56 overflow-hidden">
              <img
                src={lithiumBankImg}
                alt="Lithium Battery Bank & Inverter Wall"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                referrerPolicy="no-referrer"
              />
              <div className="absolute top-3 left-3 bg-slate-950/80 backdrop-blur-md border border-slate-800 text-cyan-400 text-[11px] font-bold px-3 py-1 rounded-full flex items-center gap-1">
                <Zap className="w-3 h-3" /> Plant Room Setup
              </div>
            </div>
            <div className="p-6 space-y-3 flex-grow flex flex-col justify-between">
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-white group-hover:text-cyan-400 transition-colors">
                  Wall-Mounted LiFePO4 Battery & Hybrid Inverter
                </h3>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Neat industrial trunking with surge protection devices (SPD), earthing rods, and 6,000+ cycle LiFePO4 battery wall packs.
                </p>
              </div>
              <div className="pt-3 border-t border-slate-900 flex items-center justify-between text-xs text-cyan-400 font-semibold">
                <span>15kWh Lithium Storage</span>
                <CheckCircle2 className="w-4 h-4 text-cyan-400" />
              </div>
            </div>
          </div>

          {/* Card 3: COREN Field Engineer */}
          <div className="bg-slate-950 border border-slate-800 rounded-3xl overflow-hidden shadow-xl hover:border-emerald-500/40 transition-all flex flex-col group">
            <div className="relative h-56 overflow-hidden">
              <img
                src={engineerImg}
                alt="COREN Certified Solar Field Engineer"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                referrerPolicy="no-referrer"
              />
              <div className="absolute top-3 left-3 bg-slate-950/80 backdrop-blur-md border border-slate-800 text-amber-400 text-[11px] font-bold px-3 py-1 rounded-full flex items-center gap-1">
                <Wrench className="w-3 h-3" /> COREN Certified
              </div>
            </div>
            <div className="p-6 space-y-3 flex-grow flex flex-col justify-between">
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-white group-hover:text-amber-400 transition-colors">
                  On-Site Technical DB & Load Audit
                </h3>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Every project begins with a 24-point electrical audit measuring earthing resistance, phase balancing, and cable distance before installation.
                </p>
              </div>
              <div className="pt-3 border-t border-slate-900 flex items-center justify-between text-xs text-amber-400 font-semibold">
                <span>100% Safety Compliance</span>
                <CheckCircle2 className="w-4 h-4 text-amber-400" />
              </div>
            </div>
          </div>

          {/* Card 4: Family Lifestyle / Zero-Noise Living */}
          <div className="bg-slate-950 border border-slate-800 rounded-3xl overflow-hidden shadow-xl hover:border-emerald-500/40 transition-all flex flex-col group">
            <div className="relative h-56 overflow-hidden">
              <img
                src={happyFamilyImg}
                alt="Nigerian Family Enjoying 24/7 Solar Power at Night"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                referrerPolicy="no-referrer"
              />
              <div className="absolute top-3 left-3 bg-slate-950/80 backdrop-blur-md border border-slate-800 text-purple-400 text-[11px] font-bold px-3 py-1 rounded-full flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> 24/7 Zero-Noise
              </div>
            </div>
            <div className="p-6 space-y-3 flex-grow flex flex-col justify-between">
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-white group-hover:text-purple-400 transition-colors">
                  24/7 Uninterrupted Home Comfort & ACs
                </h3>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Zero generator smoke, zero noise pollution, and cool air conditioning all night long without worrying about Band A grid tariffs.
                </p>
              </div>
              <div className="pt-3 border-t border-slate-900 flex items-center justify-between text-xs text-purple-400 font-semibold">
                <span>Zero Fumes or Fuel Bills</span>
                <CheckCircle2 className="w-4 h-4 text-purple-400" />
              </div>
            </div>
          </div>

          {/* Card 5: Commercial Enterprise Solar */}
          <div className="bg-slate-950 border border-slate-800 rounded-3xl overflow-hidden shadow-xl hover:border-emerald-500/40 transition-all flex flex-col group md:col-span-2 lg:col-span-1">
            <div className="relative h-56 overflow-hidden">
              <img
                src={commercialImg}
                alt="Commercial Solar Installation for Businesses in Lagos & Abuja"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                referrerPolicy="no-referrer"
              />
              <div className="absolute top-3 left-3 bg-slate-950/80 backdrop-blur-md border border-slate-800 text-blue-400 text-[11px] font-bold px-3 py-1 rounded-full flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Lagos & Abuja Commercial
              </div>
            </div>
            <div className="p-6 space-y-3 flex-grow flex flex-col justify-between">
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-white group-hover:text-blue-400 transition-colors">
                  Commercial & Supermarket Power Solutions
                </h3>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Protect cold-room refrigeration, POS checkout terminals, and server hardware from grid blackouts with three-phase heavy commercial solar setups.
                </p>
              </div>
              <div className="pt-3 border-t border-slate-900 flex items-center justify-between text-xs text-blue-400 font-semibold">
                <span>50kVA+ Commercial Systems</span>
                <CheckCircle2 className="w-4 h-4 text-blue-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action Banner */}
        <div className="bg-gradient-to-r from-emerald-950 via-slate-950 to-teal-950 p-8 rounded-3xl border border-emerald-500/30 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center md:text-left">
            <h4 className="text-xl font-bold text-white">Ready for a Zero-Generator Life?</h4>
            <p className="text-xs text-slate-300">Book a COREN-certified site audit anywhere in Abia, Lagos, Rivers, or Abuja today.</p>
          </div>
          <button
            onClick={() => setIsLeadModalOpen(true, 'Book Expert Energy Audit')}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black px-6 py-3.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-lg shadow-emerald-500/20 shrink-0 flex items-center gap-2"
          >
            Book Free Site Audit
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};
